import { noForwardProps } from '@apeiron/temp'
import { ApeironSimpleButton, MuiLoading } from '@apeiron/ui'
import MenuIcon from '@mui/icons-material/Menu'
import { Box, Modal, Slide, styled, useMediaQuery } from '@mui/material'
import ApeironScrollBox from '@src/components/share/apeiron/ApeironScrollBox'
import FilterScrollBox from '@src/components/share/filter/FilterScrollBox'
import { NAV_BAR_CONFIG } from '@src/constants/layout'
import theme from '@src/styles/theme'
import React, { FC, ReactNode, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(Box, noForwardProps)<{ __popupMode: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  justify-content: flex-start;
  position: relative;
`

const LayoutContainer = styled(Box)`
  ${props => props.theme.breakpoints.up('res1024')} {
    display: grid;
    grid-template-columns: 317px 1fr;
    > *:only-child {
      grid-column: 1 / -1;
    }
  }
  display: flex;
  flex-direction: row;
  flex: 1;
  height: 100%;
  justify-content: flex-start;
  min-height: 0; // Without this line Container will take whatever height it needs for its children since display: flex will automatically set min-height & min-width to aut
  position: relative;
`

const MainPanel = styled(Box, noForwardProps)<PopupModeProps>`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  width: 100%;
  ${props =>
    props.__popupMode
      ? ` 
      overflow-y: hidden;
      gap: 10px;
    `
      : ''}
`

const NavBarSticker = styled(Box, noForwardProps)<PopupModeProps>`
  ${props =>
    props.__popupMode
      ? ''
      : `
        position: sticky;
        top: ${
          NAV_BAR_CONFIG.primary.height +
          NAV_BAR_CONFIG.secondary.height +
          NAV_BAR_CONFIG.secondary.borderWidth
        }px;
        transform: translateY(-1px); // ? fixing 1px gap on zoom in
      `}
  width: 100%;
  z-index: 1;
`

const MainContainer = styled(Box, noForwardProps)<PopupModeProps>`
  display: grid;
  grid-template-rows: auto minmax(0px, 1fr) auto;
  height: 100%;
  min-height: 0;
  overflow-y: hidden;
  width: 100%;

  ${props => props.theme.breakpoints.up('res1024')} {
    padding: ${props => (props.__popupMode ? '0px 20px' : '20px')};
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    padding: 0px 10px 12px 10px;

    ${props =>
      !props.__popupMode &&
      `
      max-height: calc(
        100vh -
          ${
            NAV_BAR_CONFIG.primary.height +
            NAV_BAR_CONFIG.secondary.height +
            NAV_BAR_CONFIG.tertiary.height +
            66 +
            NAV_BAR_CONFIG.secondary.borderWidth
          }px
      );
    `}
  }
`

const ActionBar = styled(Box)`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px 0px;
  width: 100%;
`

const Icon = styled(MenuIcon)`
  font-size: 32px;
`

const FilterButton = styled(ApeironSimpleButton)`
  .Mui-selected,
  :hover {
    background-color: #ffffff11;
  }
  background-color: transparent;
  border: 1px solid #ffffff11;
  font-size: 18px;
  font-weight: 400;
  gap: 10px;
  padding: 4px 20px;
  margin-left: 12px;
  width: unset;
`

const LoadingContainer = styled(Box)`
  position: relative;
  width: 100%;
  min-height: 300px;
`

const SidePanel = styled(ApeironScrollBox, noForwardProps)<PopupModeProps>`
  background-color: #dfdfdf;
  border-right: 2px solid #151615;
  min-width: fit-content;
  overflow: auto;
  position: sticky;

  transform: translateY(-1px); // ? fixing 1px gap on zoom in
  max-height: calc(
    100vh -
      ${NAV_BAR_CONFIG.primary.height +
      NAV_BAR_CONFIG.secondary.height +
      NAV_BAR_CONFIG.secondary.borderWidth}px
  );
  ${props =>
    !props.__popupMode
      ? `
  top: ${
    NAV_BAR_CONFIG.primary.height +
    NAV_BAR_CONFIG.secondary.height +
    NAV_BAR_CONFIG.secondary.borderWidth
  }px;
  `
      : 'top: 0px;'}

  // Reset these two properties to their initial values and use webkit scrollbar styling
  scrollbar-color: initial;
  scrollbar-width: initial;
`

const DesktopHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 10px;
`

const MobileHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`

const BottomBar = styled(Box)`
  width: 100%;
  min-height: 0;
  min-width: 0px;
  margin-top: 10px;
  margin-bottom: 10px;
`

const GrowBox = styled(Box)`
  min-height: 0;
  overflow-y: auto;
  width: 100%;
  position: relative;
`

const StyledMuiLoading = styled(MuiLoading)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`

const FilterPopup = styled(Modal)`
  top: unset;
  height: 85vh;

  border-top-right-radius: 10px;
  border-top-left-radius: 10px;
`

const Loading = () => {
  return (
    <LoadingContainer>
      <StyledMuiLoading loading />
    </LoadingContainer>
  )
}

const ListTemplate: FC<Props> = props => {
  const {
    bottomBar,
    className,
    filter,
    headerLeft,
    loadingContent = false,
    mainContent,
    popupMode = false,
    sortDropDown,
    thirdNavBar
  } = props

  const { t } = useTranslation()

  const mobile = useMediaQuery(theme.breakpoints.down('res1024'))

  const [openMobileFilter, setOpenMobileFilter] = useState<boolean>(false)

  const handleOpenFilter = useCallback(() => {
    setOpenMobileFilter(true)
  }, [])

  const handleCloseFilter = useCallback(() => {
    setOpenMobileFilter(false)
  }, [])

  return (
    <Container __popupMode={popupMode} className={className}>
      <LayoutContainer className='Custom-Layout-Container'>
        {!mobile ? (
          <>
            {filter && <SidePanel __popupMode={popupMode}>{filter}</SidePanel>}
            <MainPanel className='Custom-Main-Panel' __popupMode={popupMode}>
              {thirdNavBar && (
                <NavBarSticker __popupMode={popupMode}>
                  {thirdNavBar}
                </NavBarSticker>
              )}
              <MainContainer
                className='Custom-Main-Container'
                __popupMode={popupMode}
              >
                <DesktopHeader>
                  {headerLeft}
                  <Box>
                    {sortDropDown}
                    {mobile && (
                      <FilterButton onClick={handleOpenFilter}>
                        {t('common.button.filter')}
                      </FilterButton>
                    )}
                  </Box>
                </DesktopHeader>
                <GrowBox>{loadingContent ? <Loading /> : mainContent}</GrowBox>
                {bottomBar && <BottomBar>{bottomBar}</BottomBar>}
              </MainContainer>
            </MainPanel>
          </>
        ) : (
          <>
            <MainPanel className='Custom-Main-Panel' __popupMode={popupMode}>
              {thirdNavBar && (
                <NavBarSticker __popupMode={popupMode}>
                  {thirdNavBar}
                </NavBarSticker>
              )}
              <ActionBar className='Custom-Action-Bar'>
                {filter && (
                  <FilterButton onClick={handleOpenFilter}>
                    <Icon />
                    {t('common.button.filter')}
                  </FilterButton>
                )}
                {sortDropDown}
              </ActionBar>
              <MainContainer
                className='Custom-Main-Container'
                __popupMode={popupMode}
              >
                {headerLeft && <MobileHeader>{headerLeft}</MobileHeader>}
                <GrowBox>{loadingContent ? <Loading /> : mainContent}</GrowBox>
                {bottomBar && <BottomBar>{bottomBar}</BottomBar>}

                {filter && (
                  <FilterPopup
                    open={openMobileFilter}
                    onClose={handleCloseFilter}
                    keepMounted
                  >
                    <Slide direction='up' in={openMobileFilter}>
                      <FilterScrollBox onClose={handleCloseFilter} tabIndex={0}>
                        {filter}
                      </FilterScrollBox>
                    </Slide>
                  </FilterPopup>
                )}
              </MainContainer>
            </MainPanel>
          </>
        )}
      </LayoutContainer>
    </Container>
  )
}

type PopupModeProps = {
  __popupMode?: boolean
}

type Props = {
  bottomBar?: ReactNode
  className?: string
  filter: ReactNode
  headerLeft?: ReactNode
  loadingContent?: boolean
  mainContent: ReactNode
  popupMode?: boolean
  sortDropDown: ReactNode
  thirdNavBar?: ReactNode
}

export default ListTemplate
